import { KeyboardEvent, useId, useRef } from 'react';

export interface TabItem {
  key: string;
  label: string;
}

interface Props {
  tabs: TabItem[];
  value: string;
  onChange: (key: string) => void;
  /** Shared prefix so panels can be linked back to their tab. */
  idBase?: string;
}

export const tabId = (base: string, key: string) => `${base}-tab-${key}`;
export const panelId = (base: string, key: string) => `${base}-panel-${key}`;

/**
 * A real tablist: `role`, `aria-selected`, roving tabindex and arrow/Home/End
 * navigation. The previous version was three plain buttons, which announced as
 * unrelated controls and could only be reached one Tab press at a time.
 */
export function Tabs({ tabs, value, onChange, idBase }: Props) {
  const generated = useId();
  const base = idBase ?? generated;
  const refs = useRef<Record<string, HTMLButtonElement | null>>({});

  function handleKeyDown(e: KeyboardEvent<HTMLDivElement>) {
    const current = tabs.findIndex(t => t.key === value);
    if (current < 0) return;

    let next: number;
    switch (e.key) {
      case 'ArrowRight': next = (current + 1) % tabs.length; break;
      case 'ArrowLeft':  next = (current - 1 + tabs.length) % tabs.length; break;
      case 'Home':       next = 0; break;
      case 'End':        next = tabs.length - 1; break;
      default: return;
    }

    e.preventDefault();
    const key = tabs[next].key;
    onChange(key);
    refs.current[key]?.focus();
  }

  return (
    <div className="border-b border-gray-200 dark:border-gray-700">
      <div role="tablist" onKeyDown={handleKeyDown} className="flex gap-0 -mb-px">
        {tabs.map(tab => {
          const selected = tab.key === value;
          return (
            <button
              key={tab.key}
              ref={el => { refs.current[tab.key] = el; }}
              type="button"
              role="tab"
              id={tabId(base, tab.key)}
              aria-selected={selected}
              aria-controls={panelId(base, tab.key)}
              tabIndex={selected ? 0 : -1}
              onClick={() => onChange(tab.key)}
              className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-brand-500 ${
                selected
                  ? 'border-brand-600 text-brand-700 dark:border-brand-400 dark:text-brand-400'
                  : 'border-transparent text-gray-500 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200'
              }`}
            >
              {tab.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

/** Spread onto the element that holds a tab's content. */
export function tabPanelProps(base: string, key: string) {
  return {
    role: 'tabpanel' as const,
    id: panelId(base, key),
    'aria-labelledby': tabId(base, key),
    tabIndex: 0,
  };
}
